import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import '../CSS/DeviceManagement.css';
import Sidebar from '../Component/Sidebar';

function DeviceManagement() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');
  const [stats, setStats] = useState(null);
  
  const [newDevice, setNewDevice] = useState({
    device_key: '',
    mac_address: '',
    device_name: '',
    computer_name: '',
    location: '',
  });
  
  const [filters, setFilters] = useState({
    is_active: 'all',
    search: '',
  });

  useEffect(() => {
    fetchDevices();
    fetchStats();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (filters.is_active !== 'all') {
        params.is_active = filters.is_active === 'true';
      }
      
      if (filters.search) {
        params.search = filters.search;
      }
      
      const response = await axiosInstance.get('/api/authorized-devices/', { params });
      setDevices(response.data.results || response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/api/authorized-devices/usage_stats/');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const generateDeviceKey = async () => {
    try {
      const response = await axiosInstance.post('/api/authorized-devices/generate_device_key/');
      const key = response.data.device_key;
      setGeneratedKey(key);
      setNewDevice({ ...newDevice, device_key: key });
    } catch (err) {
      console.error('Error generating key:', err);
      alert('Failed to generate device key');
    }
  };

  const handleAddDevice = async (e) => {
    e.preventDefault();
    
    try {
      await axiosInstance.post('/api/authorized-devices/', newDevice);
      alert('Device authorized successfully! Any employee can now login from this device.');
      setShowAddForm(false);
      setNewDevice({
        device_key: '',
        mac_address: '',
        device_name: '',
        computer_name: '',
        location: '',
      });
      setGeneratedKey('');
      fetchDevices();
      fetchStats();
    } catch (err) {
      console.error('Error adding device:', err);
      alert(err.response?.data?.detail || 'Failed to authorize device');
    }
  };

  const handleDeactivateDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to deactivate this device? No one will be able to login from it.')) {
      return;
    }
    
    try {
      await axiosInstance.post(`/api/authorized-devices/${deviceId}/deactivate/`);
      alert('Device deactivated successfully');
      fetchDevices();
      fetchStats();
    } catch (err) {
      console.error('Error deactivating device:', err);
      alert('Failed to deactivate device');
    }
  };

  const handleActivateDevice = async (deviceId) => {
    try {
      await axiosInstance.post(`/api/authorized-devices/${deviceId}/activate/`);
      alert('Device activated successfully');
      fetchDevices();
      fetchStats();
    } catch (err) {
      console.error('Error activating device:', err);
      alert('Failed to activate device');
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to permanently delete this device authorization? This cannot be undone.')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/api/authorized-devices/${deviceId}/`);
      alert('Device authorization deleted successfully');
      fetchDevices();
      fetchStats();
    } catch (err) {
      console.error('Error deleting device:', err);
      alert('Failed to delete device authorization');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const downloadDeviceKeyFile = (deviceKey, deviceName) => {
    const blob = new Blob([deviceKey], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `device_key_${deviceName.replace(/\s+/g, '_')}.key`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && devices.length === 0) {
    return <div className="device-mgmt-loading">Loading devices...</div>;
  }

  return ( <div className='index-box'>
      <Sidebar />
    <div className="device-mgmt-container">
      <div className="device-mgmt-header">
        <h1 className="device-mgmt-header-title">Authorized Devices Management</h1>
        <button 
          className="device-mgmt-btn device-mgmt-btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Authorize New Device'}
        </button>
      </div>

      {/* Statistics Dashboard */}
      {stats && (
        <div className="device-mgmt-stats-dashboard">
          <div className="device-mgmt-stat-card">
            <div className="device-mgmt-stat-value">{stats.total_devices}</div>
            <div className="device-mgmt-stat-label">Total Devices</div>
          </div>
          <div className="device-mgmt-stat-card active">
            <div className="device-mgmt-stat-value">{stats.active_devices}</div>
            <div className="device-mgmt-stat-label">Active Devices</div>
          </div>
          <div className="device-mgmt-stat-card inactive">
            <div className="device-mgmt-stat-value">{stats.inactive_devices}</div>
            <div className="device-mgmt-stat-label">Inactive Devices</div>
          </div>
        </div>
      )}

      {/* Add Device Form */}
      {showAddForm && (
        <div className="device-mgmt-form-container">
          <h2 className="device-mgmt-form-title">Authorize New Device</h2>
          <p className="device-mgmt-info-text">
            Any non-admin employee can login from this device once authorized.
          </p>
          <form onSubmit={handleAddDevice} className="device-mgmt-form">
            <div className="device-mgmt-form-row">
              <div className="device-mgmt-form-group">
                <label className="device-mgmt-form-label">Device Key *</label>
                <div className="device-mgmt-key-input-wrapper">
                  <input
                    type="text"
                    className="device-mgmt-form-input"
                    value={newDevice.device_key}
                    onChange={(e) => setNewDevice({ ...newDevice, device_key: e.target.value })}
                    placeholder="Generate or enter device key"
                    required
                  />
                  <button
                    type="button"
                    className="device-mgmt-btn device-mgmt-btn-secondary"
                    onClick={generateDeviceKey}
                  >
                    Generate Key
                  </button>
                </div>
                {generatedKey && (
                  <div className="device-mgmt-generated-key-info">
                    <p className="device-mgmt-generated-key-text">
                      ✓ Key generated! Save this to: <code className="device-mgmt-generated-key-code">C:\SurvillAuth\device.key</code>
                    </p>
                    <button
                      type="button"
                      className="device-mgmt-btn device-mgmt-btn-small"
                      onClick={() => copyToClipboard(generatedKey)}
                    >
                      Copy Key
                    </button>
                    <button
                      type="button"
                      className="device-mgmt-btn device-mgmt-btn-small"
                      onClick={() => downloadDeviceKeyFile(generatedKey, newDevice.device_name || 'device')}
                    >
                      Download as File
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="device-mgmt-form-row">
              <div className="device-mgmt-form-group">
                <label className="device-mgmt-form-label">MAC Address * (format: XX:XX:XX:XX:XX:XX)</label>
                <input
                  type="text"
                  className="device-mgmt-form-input"
                  value={newDevice.mac_address}
                  onChange={(e) => setNewDevice({ ...newDevice, mac_address: e.target.value.toUpperCase() })}
                  placeholder="00:11:22:33:44:55"
                  pattern="([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})"
                  required
                />
              </div>
            </div>

            <div className="device-mgmt-form-row">
              <div className="device-mgmt-form-group">
                <label className="device-mgmt-form-label">Device Name (e.g., "Office Computer 1")</label>
                <input
                  type="text"
                  className="device-mgmt-form-input"
                  value={newDevice.device_name}
                  onChange={(e) => setNewDevice({ ...newDevice, device_name: e.target.value })}
                  placeholder="Office Computer 1"
                />
              </div>

              <div className="device-mgmt-form-group">
                <label className="device-mgmt-form-label">Computer Name</label>
                <input
                  type="text"
                  className="device-mgmt-form-input"
                  value={newDevice.computer_name}
                  onChange={(e) => setNewDevice({ ...newDevice, computer_name: e.target.value })}
                  placeholder="DESKTOP-ABC123"
                />
              </div>
            </div>

            <div className="device-mgmt-form-row">
              <div className="device-mgmt-form-group">
                <label className="device-mgmt-form-label">Location (e.g., "Main Office, Desk 5")</label>
                <input
                  type="text"
                  className="device-mgmt-form-input"
                  value={newDevice.location}
                  onChange={(e) => setNewDevice({ ...newDevice, location: e.target.value })}
                  placeholder="Main Office, Desk 5"
                />
              </div>
            </div>

            <div className="device-mgmt-form-actions">
              <button type="submit" className="device-mgmt-btn device-mgmt-btn-primary">
                Authorize Device
              </button>
              <button
                type="button"
                className="device-mgmt-btn device-mgmt-btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setNewDevice({
                    device_key: '',
                    mac_address: '',
                    device_name: '',
                    computer_name: '',
                    location: '',
                  });
                  setGeneratedKey('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="device-mgmt-filters">
        <div className="device-mgmt-filter-group">
          <label className="device-mgmt-filter-label">Search:</label>
          <input
            type="text"
            className="device-mgmt-filter-input"
            placeholder="Device name, location, or MAC"
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
            }}
          />
        </div>

        <div className="device-mgmt-filter-group">
          <label className="device-mgmt-filter-label">Filter by Status:</label>
          <select
            className="device-mgmt-filter-select"
            value={filters.is_active}
            onChange={(e) => {
              setFilters({ ...filters, is_active: e.target.value });
              setTimeout(fetchDevices, 100);
            }}
          >
            <option value="all">All Statuses</option>
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
          </select>
        </div>

        <button className="device-mgmt-btn device-mgmt-btn-secondary" onClick={fetchDevices}>
          Refresh
        </button>
      </div>

      {/* Devices Table */}
      {error && <div className="device-mgmt-error-message">{error}</div>}
      
      <div className="device-mgmt-table-container">
        <table className="device-mgmt-table">
          <thead>
            <tr>
              <th className="device-mgmt-table-header">Device Name</th>
              <th className="device-mgmt-table-header">Location</th>
              <th className="device-mgmt-table-header">MAC Address</th>
              <th className="device-mgmt-table-header">Computer Name</th>
              <th className="device-mgmt-table-header">Status</th>
              <th className="device-mgmt-table-header">Last Used</th>
              <th className="device-mgmt-table-header">Last Used By</th>
              <th className="device-mgmt-table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 ? (
              <tr>
                <td colSpan="8" className="device-mgmt-table-cell device-mgmt-no-devices">
                  No devices found
                </td>
              </tr>
            ) : (
              devices.map((device) => (
                <tr 
                  key={device.id} 
                  className={!device.is_active ? 'device-mgmt-table-row-inactive' : ''}
                >
                  <td className="device-mgmt-table-cell">
                    <strong>{device.device_name || '-'}</strong>
                  </td>
                  <td className="device-mgmt-table-cell">{device.location || '-'}</td>
                  <td className="device-mgmt-table-cell">
                    <code className="device-mgmt-mac-code">{device.mac_address}</code>
                  </td>
                  <td className="device-mgmt-table-cell">{device.computer_name || '-'}</td>
                  <td className="device-mgmt-table-cell">
                    <span className={`device-mgmt-status-badge ${device.is_active ? 'active' : 'inactive'}`}>
                      {device.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="device-mgmt-table-cell">
                    {device.last_used_at
                      ? new Date(device.last_used_at).toLocaleString()
                      : 'Never'}
                  </td>
                  <td className="device-mgmt-table-cell">
                    {device.last_used_by
                      ? device.last_used_by.username
                      : '-'}
                  </td>
                  <td className="device-mgmt-table-cell device-mgmt-actions-cell">
                    {device.is_active ? (
                      <button
                        className="device-mgmt-btn device-mgmt-btn-warning device-mgmt-btn-small"
                        onClick={() => handleDeactivateDevice(device.id)}
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        className="device-mgmt-btn device-mgmt-btn-success device-mgmt-btn-small"
                        onClick={() => handleActivateDevice(device.id)}
                      >
                        Activate
                      </button>
                    )}
                    <button
                      className="device-mgmt-btn device-mgmt-btn-danger device-mgmt-btn-small"
                      onClick={() => handleDeleteDevice(device.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

export default DeviceManagement;